/* eslint-disable max-lines */
import React, { FC, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Flex, FlexSpan, IconToggle, Menu, Popup } from "@evergis/ui";
import { useTranslation } from "react-i18next";

import { AggregationFunction, AttributeType, ClassificationType, ClassifyAttributeType } from "@evergis/api";

import {
  LayerStatisticsAttributeTitle,
  LayerStatisticsAttributeDescription,
  LayerStatisticsAttributeMenu,
  LayerStatisticsAttributeValue,
} from "./styled";

import { ClassifyChart } from "./ClassifyChart";
import { ValuePlaceholder } from "./ValuePlaceholder";
import { ChartPlaceholder } from "./ChartPlaceholder";

import { useToggle } from "../../../../hooks/useToggle";
import { useSelectFeaturesLayerName } from "../../../../hooks/selectFeatures/useSelectFeaturesLayerName";
import { useSelectedFeaturesCondition } from "./hooks/useSelectedFeaturesCondition";
import { useProjectUpdate } from "../../../../ducks/project/hooks";
import { formatNumber } from "../../../../utils/formatNumber";
import { formatDate } from "../../../../utils/date";
import { getClientData } from "../../../../ducks/project/selectors";
import { getStatistic } from "../../../../resources/statistics";
import { getClassify } from "../../../../resources/classify";

import { AttributeProps } from "./types";

// eslint-disable-next-line max-lines-per-function
export const Attribute: FC<AttributeProps> = ({ attribute }) => {
  const { t } = useTranslation("mapControls");
  const [layerStatisticsLayerName] = useSelectFeaturesLayerName();
  const [menuOpen, toggleMenuOpen] = useToggle(false);
  const [currentFunction, setCurrentFunction] = useState<AggregationFunction | undefined>();
  const projectUpdate = useProjectUpdate();
  const clientData = useSelector(getClientData);
  const processedCondition = useSelectedFeaturesCondition();

  const aggregatedFunctions = useMemo(() => {
    switch (attribute.type) {
      case AttributeType.Int32:
      case AttributeType.Int64:
      case AttributeType.Double:
        return [AggregationFunction.Min, AggregationFunction.Max, AggregationFunction.Sum, AggregationFunction.Avg];
      case AttributeType.String:
        return [AggregationFunction.DistinctCount];
      case AttributeType.DateTime:
        return [AggregationFunction.DistinctCount, AggregationFunction.First, AggregationFunction.Last];
      default:
        return [];
    }
  }, [attribute.type]);

  const classifyType = useMemo(() => {
    switch (attribute.type) {
      case AttributeType.Int32:
      case AttributeType.Int64:
      case AttributeType.Double:
        return ClassificationType.EqualInterval;
      case AttributeType.String:
      case AttributeType.DateTime:
        return ClassificationType.Unique;
      default:
        return undefined;
    }
  }, [attribute.type]);

  const attributeType = useMemo(() => {
    switch (attribute.type) {
      case AttributeType.Int32:
      case AttributeType.Int64:
      case AttributeType.Double:
        return ClassifyAttributeType.Decimal;
      case AttributeType.String:
        return ClassifyAttributeType.Text;
      case AttributeType.DateTime:
        return ClassifyAttributeType.DateTime;
      default:
        return undefined;
    }
  }, [attribute.type]);

  const {
    data: statisicsData,
    loading: statisticsLoading,
    error: statisticsError,
    refetch: refetchStatistics,
  } = getStatistic.use({
    input: {
      name: layerStatisticsLayerName,
      attribute: attribute.name,
      condition: processedCondition,
      types: aggregatedFunctions,
    },
    immediate: false,
  });

  const {
    data: classifyData,
    loading: classifyLoading,
    error: classifyError,
    refetch: refetchClassify,
  } = getClassify.use({
    input: {
      name: layerStatisticsLayerName,
      classes: 12,
      type: classifyType,
      attribute: attribute.name,
      condition: processedCondition,
      attributeType,
    },
    immediate: false,
  });

  const chartData = useMemo(() => {
    if (!classifyData) {
      return [];
    }

    if ([AttributeType.String, AttributeType.DateTime].includes(attribute.type)) {
      return classifyData.classifyResult.map(result => ({
        groupName:
          attribute.type === AttributeType.DateTime
            ? formatDate(result.value, { dateFormat: "dd.MM.yyyy" })
            : result.value,
        0: {
          value: result.count,
        },
      }));
    }

    return Array.from({ length: classifyData.classifyResult.length - 1 }, (_, index) => ({
      groupName: classifyData.classifyResult[index === 0 ? index : index + 1].value,
      0: {
        value: classifyData.classifyResult[index + 1].count,
      },
    }));
  }, [attribute.type, classifyData]);

  const attributeValue = useMemo(() => {
    return statisicsData?.statisticsResult?.find(item => item.type === currentFunction)?.value;
  }, [statisicsData, currentFunction]);

  const menuOptions = useMemo(() => {
    switch (attribute.type) {
      case AttributeType.Int32:
      case AttributeType.Int64:
      case AttributeType.Double:
        return [
          {
            text: t("min"),
            description: "MIN",
            value: AggregationFunction.Min,
          },
          {
            text: t("max"),
            description: "MAX",
            value: AggregationFunction.Max,
          },
          {
            text: t("sum"),
            description: "SUM",
            value: AggregationFunction.Sum,
          },
          {
            text: t("avg"),
            description: "AVG",
            value: AggregationFunction.Avg,
          },
        ];
      case AttributeType.String:
        return [
          {
            text: t("uniqueCount"),
            description: "DISTINCTCOUNT",
            value: AggregationFunction.DistinctCount,
          },
        ];
      case AttributeType.DateTime:
        return [
          {
            text: t("uniqueCount"),
            description: "DISTINCTCOUNT",
            value: AggregationFunction.DistinctCount,
          },
          {
            text: t("first"),
            description: "FIRST",
            value: AggregationFunction.First,
          },
          {
            text: t("last"),
            description: "LAST",
            value: AggregationFunction.Last,
          },
        ];
      default:
        return [];
    }
  }, [attribute.type, t]);

  useEffect(() => {
    switch (attribute.type) {
      case AttributeType.Int32:
      case AttributeType.Int64:
      case AttributeType.Double:
        setCurrentFunction(AggregationFunction.Sum);
        break;
      case AttributeType.String:
        setCurrentFunction(AggregationFunction.DistinctCount);
        break;
      case AttributeType.DateTime:
        setCurrentFunction(AggregationFunction.DistinctCount);
        break;
      default:
        break;
    }
  }, [attribute.type]); // eslint-disable-line

  useEffect(() => {
    refetchStatistics();
    refetchClassify();
  }, [layerStatisticsLayerName, processedCondition]); // eslint-disable-line

  useEffect(() => {
    if (clientData?.statisticsFunctions?.[layerStatisticsLayerName]?.[attribute.name]) {
      setCurrentFunction(clientData.statisticsFunctions[layerStatisticsLayerName][attribute.name]);
    }
  }, [clientData]); // eslint-disable-line

  return (
    <FlexSpan style={{ width: "100%" }}>
      <Flex column style={{ gap: "0.5rem", width: "100%" }}>
        <FlexSpan style={{ width: "100%" }}>
          <Flex style={{ gap: "0.5rem", flexWrap: "unset", width: "100%" }}>
            <FlexSpan style={{ width: "1rem" }}>{attribute.icon}</FlexSpan>
            <FlexSpan style={{ flexGrow: 1 }}>
              <Flex column style={{ gap: "0.25rem" }}>
                <FlexSpan>
                  <LayerStatisticsAttributeTitle>{attribute.header}</LayerStatisticsAttributeTitle>
                </FlexSpan>
                <FlexSpan>
                  <LayerStatisticsAttributeDescription>
                    {menuOptions.find(item => item.value === currentFunction)?.text}
                  </LayerStatisticsAttributeDescription>
                </FlexSpan>
              </Flex>
            </FlexSpan>
            {menuOptions.length > 0 && (
              <FlexSpan>
                <Popup
                  zIndex={101}
                  anchor={ref => (
                    <IconToggle kind="switcher" innerRef={ref} isSelected={menuOpen} onClick={toggleMenuOpen} />
                  )}
                  isVisible={menuOpen}
                  onRequestClose={toggleMenuOpen}
                >
                  <LayerStatisticsAttributeMenu>
                    <Menu
                      options={menuOptions}
                      selectOption={async options => {
                        toggleMenuOpen();

                        await projectUpdate({
                          content: {
                            clientData: {
                              ...clientData,
                              statisticsFunctions: {
                                ...clientData?.statisticsFunctions,
                                [layerStatisticsLayerName]: {
                                  ...clientData?.statisticsFunctions?.[layerStatisticsLayerName],
                                  [attribute.name]: options?.[0]?.value,
                                },
                              },
                            },
                          },
                        });
                      }}
                      selectedOptions={
                        menuOptions.find(item => item.value === currentFunction)
                          ? [menuOptions.find(item => item.value === currentFunction)]
                          : []
                      }
                    />
                  </LayerStatisticsAttributeMenu>
                </Popup>
              </FlexSpan>
            )}
          </Flex>
        </FlexSpan>
        <FlexSpan style={{ width: "100%", padding: "0 0.5rem 0 1.5rem", boxSizing: "border-box" }}>
          <Flex style={{ gap: "1rem", width: "100%" }}>
            <FlexSpan style={{ flexGrow: 1, alignSelf: statisticsLoading || statisticsError ? "center" : undefined }}>
              {statisticsLoading || statisticsError ? (
                <ValuePlaceholder error={Boolean(statisticsError)} />
              ) : (
                <Flex style={{ alignItems: "baseline", gap: "0.25rem" }}>
                  <FlexSpan>
                    <LayerStatisticsAttributeValue>
                      {attributeValue !== undefined
                        ? [AggregationFunction.First, AggregationFunction.Last].includes(currentFunction)
                          ? formatDate(attributeValue, { dateFormat: "dd.MM.yyyy" })
                          : formatNumber(Math.round(attributeValue * 100) / 100)
                        : "—"}
                    </LayerStatisticsAttributeValue>
                  </FlexSpan>
                  {attribute.unit && attributeValue !== undefined && (
                    <FlexSpan>
                      <LayerStatisticsAttributeDescription>{attribute.unit}</LayerStatisticsAttributeDescription>
                    </FlexSpan>
                  )}
                </Flex>
              )}
            </FlexSpan>
            <FlexSpan>
              {classifyLoading || classifyError ? (
                <ChartPlaceholder error={Boolean(classifyError)} />
              ) : (
                <ClassifyChart
                  data={chartData}
                  classifyData={classifyData}
                  isNumberValue={[AttributeType.Int32, AttributeType.Int64, AttributeType.Double].includes(
                    attribute.type,
                  )}
                />
              )}
            </FlexSpan>
          </Flex>
        </FlexSpan>
      </Flex>
    </FlexSpan>
  );
};
